import axios, { type AxiosRequestConfig } from 'axios';
import { siteConfig } from './config';
import { clearTokens } from './token-storage';
import { showToast } from '../context/ToastContext';

// ─── Axios instance ───────────────────────────────────────────────────────────

const api = axios.create({
  baseURL: siteConfig.apiUrl,
  headers: { 'Content-Type': 'application/json' },
  // Send cookies with requests if needed
  withCredentials: true,
});

// ─── Request interceptor ──────────────────────────────────────────────────────

api.interceptors.request.use(
  (config) => {
    // Attach language
    const currentLang = localStorage.getItem('vpnmanage_lang') ?? 'vi';
    config.headers['Accept-Language'] = currentLang;

    return config;
  },
  (error: unknown) => Promise.reject(error instanceof Error ? error : new Error(String(error))),
);

// ─── Response interceptor + Auto-refresh ────────────────────────────────────

let isRefreshing = false;
let pendingQueue: Array<{
  resolve: () => void;
  reject: (err: unknown) => void;
}> = [];

function processPendingQueue(error: unknown) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  pendingQueue = [];
}

interface RetryableRequest extends AxiosRequestConfig {
  _retry?: boolean;
  _skipToast?: boolean;
}

api.interceptors.response.use(
  (response) => {
    // Skip toast cho GET requests (read) hoặc request đánh dấu _skipToast
    const method = response.config.method?.toLowerCase();
    if (method !== 'get' && !(response.config as RetryableRequest)?._skipToast) {
      const message = response.data?.message;
      if (message) {
        showToast.success(message);
      }
    }
    return response;
  },
  async (error: unknown) => {
    const axiosError = error as {
      config?: RetryableRequest;
      response?: { status?: number; data?: { message?: string } };
      message?: string;
    };

    const status = axiosError.response?.status;
    const originalRequest = axiosError.config;

    // ── Handle 401 or 403: retry with refresh token ───────────────────────────
    if (
      (status === 401 || status === 403) &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auths/login') &&
      !originalRequest.url?.includes('/auths/refresh')
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise<void>((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        });
      }

      isRefreshing = true;
      try {
        // Refresh chỉ cần set cookie mới (httpOnly) — không cần đọc token từ body.
        await axios.post(`${siteConfig.apiUrl}/api/v1/auths/refresh`, {}, { withCredentials: true });

        processPendingQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        // Tab khác có thể vừa xoay refresh token (cookie dùng chung) → thử lại bằng cookie mới
        // trước khi đá về login.
        if ((refreshError as { response?: { status?: number } })?.response?.status === 401) {
          try {
            const res = await api(originalRequest);
            processPendingQueue(null);
            return res;
          } catch {
            // Vẫn 401 → phiên thật sự hết, đi tiếp xuống login.
          }
        }
        processPendingQueue(refreshError);
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ── Standardise error message ────────────────────────────────────────────
    const serverMessage = axiosError.response?.data?.message;
    const errorMessage =
      serverMessage ?? axiosError.message ?? 'Đã có lỗi xảy ra. Vui lòng thử lại.';

    // Skip toast error cho các request đánh dấu _skipToast (vd: auto-refresh SSE)
    if (!originalRequest?._skipToast) {
      showToast.error(errorMessage);
    }

    const enhancedError = new Error(errorMessage);
    Object.assign(enhancedError, {
      response: axiosError.response,
      status: axiosError.response?.status,
    });

    return Promise.reject(enhancedError);
  },
);

export default api;
